import { BrowserAuthError, InteractionRequiredAuthError } from '@azure/msal-browser';

import { apiTokenRequest, pca, silentTokenRedirectUri } from '~/features/auth/config.ts';

export class NotFoundError extends Error {
  constructor(message = 'Resource not found') {
    super(message);
    this.name = 'NotFoundError';
  }
}

type RequestInfo2 = RequestInfo | URL;
type RequestInit2 = Omit<RequestInit, 'body'> & { body?: RequestInit['body'] | object };
type Args = [RequestInfo2] | [RequestInfo2, RequestInit2];

// The signed-in user's API token, for callers that build their own request (the API
// documentation page attaches it to Swagger UI's "Try it out" calls).
export async function getAccessToken(): Promise<string> {
  await pca.initialize();
  const account = pca.getActiveAccount() || pca.getAllAccounts()[0];

  if (!account) {
    throw new Error('Unable to fetch. No active MSAL account found.');
  }

  const { accessToken } = await pca.acquireTokenSilent({
    ...apiTokenRequest,
    account,
    redirectUri: silentTokenRedirectUri,
  })
    .catch(async (e: unknown) => {
      if (e instanceof InteractionRequiredAuthError || e instanceof BrowserAuthError) {
        await pca.acquireTokenRedirect(apiTokenRequest);
      }

      throw e;
    });
  return accessToken;
}

export async function http<T>(...args: Args): Promise<T | undefined> {
  const accessToken = await getAccessToken();
  const [url, inputInit] = args;
  const headers = new Headers(inputInit?.headers || {});

  if (inputInit?.body !== undefined && typeof inputInit.body === 'object' && !(inputInit.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
    inputInit.body = JSON.stringify(inputInit.body);
  }

  headers.set('Authorization', `Bearer ${accessToken}`);
  const init = inputInit ? { ...inputInit, headers } : { headers };
  const r = await fetch(url, init as RequestInit);

  if (r.ok) {
    try {
      return await r.json() as T;
    }
    catch {
      return;
    }
  }

  if (r.status === 404) {
    throw new NotFoundError();
  }

  // Try to extract error message from response body (RFC 7807 Problem Details format)
  let errorMessage = `Request failed. Status: ${r.status}`;
  try {
    const errorBody = await r.json() as Record<string, unknown>;
    // Problem Details: prefer 'detail' (specific), fall back to 'title' (general)
    const apiMessage = errorBody['detail'] ?? errorBody['title'];
    if (typeof apiMessage === 'string') {
      errorMessage = apiMessage;
    }
  }
  catch {
    // Response body is not JSON or couldn't be parsed
  }

  throw new Error(errorMessage, { cause: { status: r.status } });
}
